import React, { useMemo } from 'react';
import stopIcon from '../../img/_Stop.png';
import yieldIcon from '../../img/_Yield.png';
import goIcon from '../../img/_Go.png';
import closeIcon from '../../img/pretty-close-2.png';
import lightPDFIcon from './assets/pdf-light.png';
import darkPDFIcon from './assets/pdf-dark.png';
import modifiedIcon from './assets/modified.png';
import notModifiedIcon from './assets/not-modified.png';
import styles from './CollapsedMatchBlockStyles';
import ZoneName1 from './ZoneName1';
import ZoneName2 from './ZoneName2';
import DocName1 from './DocName1';
import DocName2 from './DocName2';
import ExpandButton from './ExpandButton';

// (getUserModifiedIcon is intentionally omitted here; Expanded variants render a similar indicator)

// Module-level helpers to avoid recreating these on each render
const headerSynonyms = {
  qty: 'Quantity',
  quantity: 'Quantity',
  quant: 'Quantity',
  total: 'Total',
  acv: 'ACV',
  rcv: 'RCV',
  desc: 'Description',
  description: 'Description',
};

const parseNumberWithUnit = (val) => {
  if (!val || typeof val !== 'string') return { num: NaN, unit: '' };
  const cleaned = val.replace(/[()]/g, '').replace(/,/g, '').trim();
  const match = cleaned.match(/^(-?[\d.]+)\s*([a-zA-Z%°\/]*)$/);
  if (!match) return { num: NaN, unit: '' };
  let num = parseFloat(match[1]);
  if (val.includes('(') && val.includes(')')) num *= -1;
  return { num, unit: match[2] || '' };
};

const getValueByHeader = (values, header) => {
  const canonical = headerSynonyms[header.toLowerCase()] || header;
  const keys = Object.keys(values || {});
  const matchKey = keys.find(k => headerSynonyms[k.toLowerCase()] === canonical || k.toLowerCase() === canonical.toLowerCase());
  return matchKey ? values[matchKey] : '-';
};

const CollapsedMatchBlock = ({
  match,
  cleanParsedEstData,
  setCleanParsedEstData,
  cleanParsedInsData,
  setCleanParsedInsData,
  docname1,
  docname2,
  claimId,
  onToggle,
  visibleDocs,
  toggleDoc,
  lineItemUsageMap,
  doc2NoMatch
}) => {
  const system = match['User Modified'] || {};
  const doc1Ids = system?.doc1?.ids || [];
  const doc2Ids = system?.doc2?.ids || [];
  const designation = system?.Designation;

  const estLineItemMap = useMemo(() => {
    const map = {};
    (cleanParsedEstData?.lineItems || []).forEach(obj => {
      const id = Object.keys(obj)[0];
      map[id] = obj[id];
    });
    return map;
  }, [cleanParsedEstData?.lineItems]);

  const insLineItemMap = useMemo(() => {
    const map = {};
    (cleanParsedInsData?.lineItems || []).forEach(obj => {
      const id = Object.keys(obj)[0];
      map[id] = obj[id];
    });
    return map;
  }, [cleanParsedInsData?.lineItems]);

  const usageWarnings1 = useMemo(() => doc1Ids.filter(id => (lineItemUsageMap[id] || 0) > 1), [doc1Ids, lineItemUsageMap]);
  const usageWarnings2 = useMemo(() => doc2Ids.filter(id => (lineItemUsageMap[id] || 0) > 1), [doc2Ids, lineItemUsageMap]);

  const statusIcon = useMemo(() => {
    const system = match["System Detected"];
    const user = match["User Modified"];
    const userModified = JSON.stringify(system) !== JSON.stringify(user);

    // Priority check: doc2NoMatch overrides everything
    if (doc2NoMatch) {
      return (
        <img
          title="This is a no-match."
          src={stopIcon}
          alt="No Match"
          style={styles.statusIcon}
        />
      );
    }

    if (userModified) {
      const hasNoDoc2 = !user?.doc2?.ids || user.doc2.ids.length === 0;

      if (hasNoDoc2) {
        return (
          <img
            title="This is a no-match."
            src={stopIcon}
            alt="No Match"
            style={styles.statusIcon}
          />
        );
      } else {
        return (
          <img
            title="This match was modified by the user."
            src={yieldIcon}
            alt="Similar"
            style={styles.statusIcon}
          />
        );
      }
    }

    switch (designation) {
      case 'Match':
        return (
          <img
            title="The description and zone are semantically the same."
            src={goIcon}
            alt="Match"
            style={styles.statusIcon}
          />
        );
      case 'Close':
        return (
          <img
            title="The description and zone are semantically the same but numeric values differ."
            src={closeIcon}
            alt="Close"
            style={styles.statusIcon}
          />
        );
      case 'Similar':
        return (
          <img
            title="The description and/or the zone are close but not the same."
            src={yieldIcon}
            alt="Similar"
            style={styles.statusIcon}
          />
        );
      default:
        return (
          <img
            title="There was not a clear match."
            src={stopIcon}
            alt="No Match"
            style={styles.statusIcon}
          />
        );
    }
  }, [match, designation, doc2NoMatch]);
  

  const crossColumnFinancialDeltas = useMemo(() => {
    const headers = ['ACV', 'RCV', 'Total'];
    const results = [];
    for (let h1 of headers) {
      let sum1 = 0, unit1Set = new Set(), valid1 = false;
      doc1Ids.forEach(id => {
        const item = estLineItemMap[id];
        if (item) {
          const val = getValueByHeader(item.values || {}, h1);
          const { num, unit } = parseNumberWithUnit(val);
          if (!isNaN(num)) {
            sum1 += num;
            unit1Set.add(unit);
            valid1 = true;
          }
        }
      });
      for (let h2 of headers) {
        let sum2 = 0, unit2Set = new Set(), valid2 = false;
        doc2Ids.forEach(id => {
          const item = insLineItemMap[id];
          if (item) {
            const val = getValueByHeader(item.values || {}, h2);
            const { num, unit } = parseNumberWithUnit(val);
            if (!isNaN(num)) {
              sum2 += num;
              unit2Set.add(unit);
              valid2 = true;
            }
          }
        });
        const unitsMatch = unit1Set.size <= 1 && unit2Set.size <= 1 &&
          [...unit1Set][0] === [...unit2Set][0];
        const unit = unitsMatch ? [...unit1Set][0] || '' : '';
        if (valid1 && valid2 && unitsMatch) {
          const diff = (sum1 - sum2).toFixed(2);
          results.push(`${h1} - ${h2}: ${diff} ${unit}`.trim());
        }
      }
    }
    return results;
  }, [doc1Ids, doc2Ids, estLineItemMap, insLineItemMap]);

  const statusShadow = useMemo(() => {
    const system = match["System Detected"];
    const user = match["User Modified"];
    const userModified = JSON.stringify(system) !== JSON.stringify(user);

    if (userModified) {
      const hasNoDoc2 = !user?.doc2?.ids || user.doc2.ids.length === 0;
      const hasZero = crossColumnFinancialDeltas.some(line => line.startsWith('Total - Total:') && line.includes('0.00'));

      if (hasNoDoc2 || doc2NoMatch) {
        return '5px 3px 6px rgba(255, 0, 0, 0.3)'; // red for no-match
      } else if (hasZero) {
        return '5px 3px 6px rgba(0, 128, 0, 0.3)'; // green for match
      } else {
        return '5px 3px 6px rgba(255, 215, 0, 0.3)'; // yellow for similar
      }
    }

    // Fallback for unmodified matches
    if (!designation) return '';
    switch (designation) {
      case 'Match':
      case 'Close': // Treat "Close" like "Match"
        return '5px 3px 6px rgba(0, 128, 0, 0.3)';
      case 'Similar':
        return '5px 3px 6px rgba(255, 215, 0, 0.3)';
      default:
        return '5px 3px 6px rgba(255, 0, 0, 0.3)';
    }
  }, [match, designation, doc2NoMatch, crossColumnFinancialDeltas]);
  

  const headerSynonyms = {
    qty: 'Quantity',
    quantity: 'Quantity',
    quant: 'Quantity',
    total: 'Total',
    acv: 'ACV',
    rcv: 'RCV',
    desc: 'Description',
    description: 'Description',
  };

  

  // Helper: get first and last key-value pairs from values object
  const getFirstAndLastKV = (valuesObj) => {
    if (!valuesObj) return { firstValue: '-', lastKey: '-', lastValue: '-' };
    const keys = Object.keys(valuesObj);
    if (keys.length === 0) return { firstValue: '-', lastKey: '-', lastValue: '-' };
    const firstValue = valuesObj[keys[0]] || '-';
    const lastKey = keys[keys.length - 1];
    const lastValue = valuesObj[lastKey] || '-';
    return { firstValue, lastKey, lastValue };
  };

  const renderDocBlock = (ids, map, isDoc1, docName, ZoneNameComp, toggleKey) => {
    if (!ids.length) return null;
    const currentWarnings = isDoc1 ? usageWarnings1 : usageWarnings2;

    return (
      <div style={{ ...styles.docBlock, ...(isDoc1 ? styles.doc1 : styles.doc2) }}>
        <div style={isDoc1 ? styles.doc1InfoContainer : styles.doc2InfoContainer}>
          {isDoc1 ? (
            <DocName1 docname1={docName} claimId={claimId} />
          ) : (
            <DocName2 docname2={docName} claimId={claimId} />
          )}
          <ZoneNameComp
            li={map[ids[0]] ? { [ids[0]]: map[ids[0]] } : null}
            cleanParsedEstData={isDoc1 ? cleanParsedEstData : null}
            setCleanParsedEstData={isDoc1 ? setCleanParsedEstData : null}
            cleanParsedInsData={!isDoc1 ? cleanParsedInsData : null}
            setCleanParsedInsData={!isDoc1 ? setCleanParsedInsData : null}
            claimId={claimId}
          />
          <br/>
          <button
            style={isDoc1 ? styles.showRefDoc1 : styles.showRefDoc2}
            onClick={() => toggleDoc(toggleKey)}
            title="Open the file."
          >
            <img
              src={isDoc1 ? lightPDFIcon : darkPDFIcon}
              alt="doc icon"
              style={{ width: '16px', height: '16px', marginRight: '6px', verticalAlign: 'middle' }}
            />
            {visibleDocs?.[toggleKey] ? 'Hide Reference File' : 'View Reference File'}
          </button>

        </div>

        <div style={isDoc1 ? styles.li1InfoContainer : styles.li2InfoContainer}>
          
          {/* Line items */}
          {ids.map((id) => {
            const item = map[id];
            if (!item) return null;
            const matchId = id;
            const valuesObj = item.values || {};
            const { firstValue, lastKey, lastValue } = getFirstAndLastKV(valuesObj);

            return (
              <div key={id} style={styles.valueRow}>
                {currentWarnings.includes(matchId) && (
                  <div style={{ color: 'orange', fontWeight: 'bold' }}>
                    Warning: This LINE ITEM is used in multiple MATCHES
                  </div>
                )}
                <p style={{ 
                  ...styles.valueCell, 
                  ...(isDoc1 ? styles.valueColor1 : styles.valueColor2) 
                }}>
                  <strong
                    style={{
                      color: isDoc1 ? '#425158' : '#839299',
                      fontWeight: '900'
                    }}
                  >DESCRIPTION:</strong> {firstValue}
                </p>

                
                <p style={{ 
                  ...styles.valueCell, 
                  ...(isDoc1 ? styles.valueColor1 : styles.valueColor2) 
                }}>                
                  <strong
                    style={{
                      color: isDoc1 ? '#425158' : '#839299',
                      fontWeight: '900'
                    }}
                  >{lastKey}:</strong> <br /> {lastValue}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.matchBlock}>
      <div style={styles.matchHeader}>{statusIcon}</div>
      <div
        style={{
          ...styles.matchDetails,
          boxShadow: statusShadow, // unified logic for shadow color
          justifyContent: doc2NoMatch ? 'flex-end' : 'flex-start',
        }}
      >
        {renderDocBlock(doc1Ids, estLineItemMap, true, docname1, ZoneName1, 'doc1')}
        {renderDocBlock(doc2Ids, insLineItemMap, false, docname2, ZoneName2, 'doc2')}
      </div>

      <div style={{ padding: '0px 8px 0px 24px' }}>
        <div style={styles.crossColumnMath}>
        {crossColumnFinancialDeltas.map((line, i) => {
          const [label, value] = line.split(/:(.+)/); // split on first colon only
          const numericValue = parseFloat(value.replace(/[^0-9.-]/g, '')); // strip non-numeric chars

          // Transform "RCV - Total" → "∆(RCV, Total)"
          const transformedLabel = label.replace(/(.+?) - (.+)/, '∆($1, $2)');

          // Always display the absolute value as a dollar amount
          const displayValue = Math.abs(numericValue).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
          });

          return (
            <span key={i} style={{ fontSize: 17 }}>
              <span
                style={{
                  color: '#425158',
                  padding: '0px 2px',
                  fontWeight: 'normal',
                }}
              >
                {transformedLabel}:
              </span>{' '}
              <span
                style={{
                  color: numericValue > 0 ? 'red' : 'green',
                  padding: '0px 2px',
                  fontWeight: '900',
                }}
              >
                {displayValue}
              </span>
            </span>
          );
        })}

        </div>
        {doc2NoMatch ? null : (
          <ExpandButton expanded={match.expanded} onClick={onToggle} />
        )}
      </div>
    </div>
  );
  
};

export default React.memo(CollapsedMatchBlock);

